import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import type { Driver } from '../lib/types.ts';

type Draft = {
  name: string;
  email: string;
  phone: string;
};

function emptyDraft(): Draft {
  return { name: '', email: '', phone: '' };
}

export function CompanyDrivers() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'drivers'],
    queryFn: () => companyApi.get<{ drivers: Driver[] }>('/api/me/drivers'),
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Driver | null>(null);
  const [inviteResult, setInviteResult] = useState<{ id: string; sent: boolean } | null>(null);

  const inviteMutation = useMutation({
    mutationFn: (id: string) => companyApi.post(`/api/me/drivers/${id}/invite`),
    onSuccess: (_data, id) => {
      setInviteResult({ id, sent: true });
      void qc.invalidateQueries({ queryKey: ['me', 'drivers'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => companyApi.delete(`/api/me/drivers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'drivers'] }),
  });

  if (isLoading) return <CenteredSpinner />;

  const drivers = data?.drivers ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Drivers</h1>
          <p className="mt-1 text-sm text-slate-500">
            Add drivers that work for your company. Each driver receives their own invitation
            email so they can complete their driver-specific questionnaire.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ Add driver</Btn>
      </div>

      {drivers.length === 0 ? (
        <EmptyState
          title="No drivers yet"
          description="Add your first driver to send them an invitation."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {drivers.map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-3">
                    <Link
                      to={`/c/drivers/${d.id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {d.name}
                    </Link>
                    {d.phone && <div className="text-xs text-slate-500">{d.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{d.email}</td>
                  <td className="px-4 py-3">
                    <CompanyStatusBadge status={d.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-700">{d.progress}%</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Btn
                        size="sm"
                        variant="secondary"
                        loading={inviteMutation.isPending && inviteMutation.variables === d.id}
                        onClick={() => inviteMutation.mutate(d.id)}
                      >
                        {d.invited_at ? 'Resend invite' : 'Send invite'}
                      </Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setEditing(d)}>
                        Edit
                      </Btn>
                      <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          void (async () => {
                            if (
                              await confirm({
                                title: 'Remove driver',
                                body: (
                                  <>
                                    Are you sure you want to remove{' '}
                                    <span className="font-semibold">{d.name}</span>?
                                  </>
                                ),
                                confirmLabel: 'Remove',
                                danger: true,
                              })
                            )
                              deleteMutation.mutate(d.id);
                          })();
                        }}
                      >
                        Remove
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inviteMutation.error && (
        <div className="mt-3">
          <ErrorMessage error={inviteMutation.error} />
        </div>
      )}
      {inviteResult?.sent && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Invitation sent.
        </div>
      )}

      {creating && (
        <DriverEditor mode="create" onClose={() => setCreating(false)} />
      )}
      {editing && (
        <DriverEditor
          mode="edit"
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function DriverEditor({
  mode,
  existing,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing?: Driver;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() =>
    existing
      ? {
          name: existing.name,
          email: existing.email,
          phone: existing.phone ?? '',
        }
      : emptyDraft(),
  );
  const [error, setError] = useState<unknown>(null);

  function set<K extends keyof Draft>(k: K, v: Draft[K]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: draft.name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim() || null,
      };
      if (mode === 'create') {
        return companyApi.post('/api/me/drivers', payload);
      }
      return companyApi.patch(`/api/me/drivers/${existing!.id}`, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'drivers'] });
      onClose();
    },
    onError: setError,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'Add driver' : `Edit ${existing!.name}`}
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!draft.name.trim() || !draft.email.trim()}
          >
            {mode === 'create' ? 'Add driver' : 'Save'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Full name" required>
          <TextInput value={draft.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Email" required>
          <TextInput
            type="email"
            value={draft.email}
            onChange={(e) => set('email', e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <TextInput value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
        </Field>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

